import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { inventarioAPI } from '../services/api';
import { toast } from 'sonner';

const TIPOS = [
  { id: 'vacuna', label: 'Vacuna' },
  { id: 'antiparasitario', label: 'Antiparasitario' },
  { id: 'antibiotico', label: 'Antibiótico' },
  { id: 'hormona', label: 'Hormona' },
  { id: 'dispositivo', label: 'Dispositivo' },
  { id: 'suplemento', label: 'Suplemento' },
  { id: 'vitamina', label: 'Vitamina' },
  { id: 'otro', label: 'Otro' },
];
const tipoLabel = (id) => TIPOS.find(t => t.id === id)?.label || id;

export default function InventarioPage() {
  const { user } = useAuth();
  const [insumos, setInsumos] = useState([]);
  const [alertas, setAlertas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [mov, setMov] = useState({});
  const [form, setForm] = useState({ nombre: '', tipo: 'vacuna', unidad: 'dosis', stock: '', stock_minimo: '', fecha_vencimiento: '' });

  const loadData = useCallback(async () => {
    if (!user?.establecimiento_id) return;
    setLoading(true);
    try {
      const [i, a] = await Promise.all([
        inventarioAPI.listarInsumos({ establecimiento_id: user.establecimiento_id }),
        inventarioAPI.alertas(user.establecimiento_id),
      ]);
      setInsumos(i.data.insumos);
      setAlertas(a.data);
    } catch { toast.error('Error cargando inventario'); }
    setLoading(false);
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await inventarioAPI.crearInsumo({
        ...form,
        stock: parseFloat(form.stock || 0),
        stock_minimo: parseFloat(form.stock_minimo || 0),
        fecha_vencimiento: form.fecha_vencimiento || null,
        establecimiento_id: user.establecimiento_id,
      });
      toast.success('Insumo agregado');
      setShowForm(false);
      setForm({ nombre: '', tipo: 'vacuna', unidad: 'dosis', stock: '', stock_minimo: '', fecha_vencimiento: '' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear insumo'); }
  };

  const movimiento = async (insumoId, tipo) => {
    const cantidad = parseFloat(mov[insumoId] || 0);
    if (!cantidad || cantidad <= 0) { toast.error('Ingresá una cantidad válida'); return; }
    try {
      const res = await inventarioAPI.movimiento(insumoId, { tipo, cantidad });
      toast.success(`${res.data.message} — stock: ${res.data.stock}`);
      setMov({ ...mov, [insumoId]: '' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const eliminar = async (id) => {
    try { await inventarioAPI.eliminarInsumo(id); toast.success('Insumo eliminado'); loadData(); } catch { toast.error('Error'); }
  };

  if (loading) return (
    <div className="p-6 flex items-center justify-center min-h-[60vh]">
      <div className="inline-block w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const al = alertas || {};

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="inventario-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Inventario Sanitario</h2>
          <p className="text-sm text-gray-500 mt-1">Vacunas, medicamentos y suplementos con baja automática al usarlos</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="px-4 py-2 bg-cyan-600 text-white text-sm font-semibold rounded-lg hover:bg-cyan-700 transition" data-testid="btn-nuevo-insumo">+ Insumo</button>
      </div>

      {al.total_alertas > 0 && (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-4 space-y-1" data-testid="inventario-alertas">
          <h3 className="font-bold text-red-900">⚠ {al.total_alertas} alerta{al.total_alertas > 1 ? 's' : ''} de inventario</h3>
          {(al.stock_bajo || []).map(i => <p key={i.id} className="text-sm text-red-800">• Stock bajo: <strong>{i.nombre}</strong> ({i.stock} {i.unidad}, mínimo {i.stock_minimo})</p>)}
          {(al.por_vencer || []).map(i => <p key={i.id} className="text-sm text-amber-800">• Por vencer: <strong>{i.nombre}</strong> vence {i.fecha_vencimiento}</p>)}
          {(al.vencidos || []).map(i => <p key={i.id} className="text-sm text-red-900 font-semibold">• VENCIDO: {i.nombre} (venció {i.fecha_vencimiento})</p>)}
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="bg-white rounded-xl shadow-sm p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3" data-testid="form-insumo">
          <input required placeholder="Nombre (ej: Aftosa)" value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} className="border rounded-lg px-3 py-2 text-sm lg:col-span-2" data-testid="insumo-nombre" />
          <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="insumo-tipo">
            {TIPOS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <input placeholder="Unidad (dosis, ml)" value={form.unidad} onChange={e => setForm({ ...form, unidad: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="insumo-unidad" />
          <input required type="number" step="0.1" min="0" placeholder="Stock inicial" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="insumo-stock" />
          <input type="number" step="0.1" min="0" placeholder="Stock mínimo" value={form.stock_minimo} onChange={e => setForm({ ...form, stock_minimo: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="insumo-stock-min" />
          <input type="date" value={form.fecha_vencimiento} onChange={e => setForm({ ...form, fecha_vencimiento: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" title="Fecha de vencimiento" data-testid="insumo-vencimiento" />
          <button type="submit" className="px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 lg:col-span-7" data-testid="insumo-guardar">Guardar insumo</button>
        </form>
      )}

      {insumos.length === 0 && !showForm ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center" data-testid="inventario-empty">
          <p className="text-gray-500 mb-3">Tu inventario está vacío. Cargá tu primera <strong>vacuna o medicamento</strong> y el sistema descuenta el stock automáticamente cada vez que registrás un evento sanitario.</p>
          <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-cyan-600 text-white text-sm font-semibold rounded-lg hover:bg-cyan-700">Cargar mi primer insumo</button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3">Insumo</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Stock</th>
                <th className="px-4 py-3">Vencimiento</th>
                <th className="px-4 py-3">Movimiento</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {insumos.map(i => {
                const bajo = i.stock_minimo > 0 && i.stock <= i.stock_minimo;
                return (
                  <tr key={i.id} className="hover:bg-gray-50" data-testid={`insumo-row-${i.id}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">{i.nombre}</td>
                    <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 bg-cyan-50 text-cyan-800 rounded-full">{tipoLabel(i.tipo)}</span></td>
                    <td className={`px-4 py-3 font-bold ${bajo ? 'text-red-600' : 'text-gray-900'}`}>{i.stock} {i.unidad}{bajo && ' ⚠'}</td>
                    <td className="px-4 py-3 text-gray-500">{i.fecha_vencimiento || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <input type="number" step="0.1" min="0" placeholder="Cant." value={mov[i.id] || ''}
                          onChange={e => setMov({ ...mov, [i.id]: e.target.value })}
                          className="w-20 border rounded-lg px-2 py-1 text-sm" data-testid={`mov-cantidad-${i.id}`} />
                        <button onClick={() => movimiento(i.id, 'entrada')} className="px-2 py-1 text-xs bg-emerald-100 text-emerald-800 rounded-lg hover:bg-emerald-200 font-semibold" data-testid={`mov-entrada-${i.id}`}>+ Entrada</button>
                        <button onClick={() => movimiento(i.id, 'salida')} className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-lg hover:bg-red-200 font-semibold" data-testid={`mov-salida-${i.id}`}>- Salida</button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => eliminar(i.id)} className="text-gray-300 hover:text-red-500" title="Eliminar" data-testid={`del-insumo-${i.id}`}>✕</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
